import { useState, type ReactNode } from "react";
import { SearchSelect } from "@/components/SearchSelect";
import { ExchangeHero } from "@/components/ExchangeHero";

import { buttonClass } from "@/components/buttonStyles";
// Direct Markets — styled to match the API Exchange landing (light hero with a
// crimson eyebrow + headline + the converge graphic, then a badged section).
// The Product filter narrows the list (a carrier is shown only if it writes the
// selected product; BOP is written by MGT alone). The Industry filter is
// presentational. Carrier tiles + "Login now!" buttons are inert scaffolding.

// MGT's brand lockup — a mountain mark + wordmark — recreated inline to match the
// carrier's logo (demo carrier; hand-built from the supplied artwork).
function MgtLogo() {
  return (
    <span className="flex items-center gap-2.5">
      <svg viewBox="0 0 42 30" className="h-7 w-auto" aria-hidden="true">
        <path
          d="M0 29 L13 3 L22 20 L22 29 Z"
          fill="#1e293b"
          className="dark:fill-slate-300"
        />
        <path
          d="M15 29 L26 10 L42 29 Z"
          fill="#334155"
          className="dark:fill-slate-400"
        />
      </svg>
      <span className="h-6 w-px bg-slate-300 dark:bg-slate-600" />
      <span className="text-display font-extrabold tracking-tight text-slate-800 dark:text-slate-200">
        MGT
      </span>
    </span>
  );
}

interface Carrier {
  name: string;
  products: string[];
  logo?: ReactNode;
  monogram?: string;
  color?: string;
}

// Placeholder carriers. `products` drives the Product filter — BOP is written by
// MGT only, so selecting BOP surfaces MGT alone.
const CARRIERS: Carrier[] = [
  { name: "MGT Insurance", products: ["bop", "gl", "wc"], logo: <MgtLogo /> },
  {
    name: "Northbridge Insurance",
    monogram: "NB",
    color: "text-blue-600 dark:text-blue-300",
    products: ["gl", "ca"],
  },
  {
    name: "SummitPoint Insurance",
    monogram: "SP",
    color: "text-slate-700 dark:text-slate-300",
    products: ["wc", "builders_risk"],
  },
  {
    name: "Pinnacle Insurance Group",
    monogram: "PIG",
    color: "text-purple-600 dark:text-purple-300",
    products: ["ca", "surety"],
  },
  {
    name: "Harborline Insurance",
    monogram: "HL",
    color: "text-fuchsia-600 dark:text-fuchsia-300",
    products: ["gl", "surety"],
  },
];

const PRODUCT_OPTIONS = [
  { value: "bop", label: "Business Owners Policy (BOP)" },
  { value: "wc", label: "Workers' Compensation" },
  { value: "gl", label: "General Liability" },
  { value: "ca", label: "Commercial Auto" },
  { value: "builders_risk", label: "Builders Risk" },
  { value: "surety", label: "Surety" },
];

// Construction industries (extends the PMS industries fixture with the common
// trades). Presentational — the filter doesn't narrow the list yet.
const INDUSTRY_OPTIONS = [
  { value: "general_contractor", label: "General Contractor" },
  { value: "roofing", label: "Roofing" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
  { value: "hvac", label: "HVAC" },
  { value: "masonry_concrete", label: "Masonry & Concrete" },
  { value: "carpentry", label: "Carpentry" },
  { value: "painting", label: "Painting" },
  { value: "excavation", label: "Excavation & Site Work" },
  { value: "landscaping", label: "Landscaping" },
];

export function DirectMarketsPage() {
  const [product, setProduct] = useState("");
  // Presentational only — the industry filter does not narrow the list yet.
  const [industry, setIndustry] = useState("");

  const carriers = product
    ? CARRIERS.filter((c) => c.products.includes(product))
    : CARRIERS;

  return (
    <div className="min-h-full bg-brk-bg p-4 text-brk-text sm:p-6">
      <div className="flex flex-col gap-10">
        <ExchangeHero
          eyebrow="Direct Markets"
          title="Your appointed markets, one login away"
          subtitle="Filter your markets by product and appetite, and log in directly to the carrier portals."
        />

        {/* Filters */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="w-full sm:w-56">
            <SearchSelect
              label="Product"
              placeholder="All products"
              options={PRODUCT_OPTIONS}
              value={product}
              onChange={setProduct}
            />
          </div>
          <div className="w-full sm:w-64">
            <SearchSelect
              label="Industry"
              placeholder="All industries"
              options={INDUSTRY_OPTIONS}
              value={industry}
              onChange={setIndustry}
            />
          </div>
        </div>

        {/* Carriers */}
        <section className="flex flex-col gap-4">
          <h2 className="text-heading text-brk-text">Appointed carriers</h2>
          {carriers.length === 0 ? (
            <p className="text-sm text-brk-dim">
              No direct markets for this product yet.
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {carriers.map((c) => (
                <li
                  key={c.name}
                  className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 shadow-sm"
                >
                  <div className="flex h-14 items-center justify-center">
                    {c.logo ?? (
                      <span
                        aria-hidden="true"
                        className={`text-display font-extrabold tracking-tight ${c.color}`}
                      >
                        {c.monogram}
                      </span>
                    )}
                  </div>
                  <span className="text-center text-sm text-brk-dim">
                    {c.name}
                  </span>
                  {/* Inert: direct-appointment login isn't wired yet (dummy). */}
                  <button
                    type="button"
                    className={buttonClass({
                      variant: "secondary",
                      full: true,
                    })}
                  >
                    Login now!
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M14 4h6v6M20 4l-8 8" />
                      <path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
